import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { PlusIcon } from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Shell/Modal';
import { Button } from '../../components/Button';
import {
	TemplateField,
	TemplateDate,
	TemplateReactSelect,
} from '../../components/Attributes/Attributes_old';

import useAsync from '../../hooks/useAsync';
import usePrevious from '../../hooks/usePrevious';
import useModal from '../../hooks/useModal';

import {
	getCustomers,
	getAssetsAdmin,
	createBlankAdminServiceReport,
} from '../../api/admin';
import { useApp } from '../../context/AppContextBeta';
import AddOrganizationModal from './modals/AddOrganizationModal';

const TYPE_OPTIONS = [
	{ label: 'Repair', value: 'repair' },
	{ label: 'Preventive Maintenance', value: 'pm' },
	{ label: 'Inspection', value: 'inspection' },
];

const DEFAULT_FORM = {
	completedOn: new Date(),
};

const OrganizationsList = () => {
	const navigate = useNavigate();
	const { switchOrganization, organization: currentOrganization } = useApp();
	const [isCreateModalOpen, setCreateModalOpen] = useState(false);
	const [selectedOrganization, setSelectedOrganization] = useState(null);
	const [form, setForm] = useState(DEFAULT_FORM);

	const {
		open: isAddOrgModalOpen,
		setOpen: onOpenAddOrgModal,
	} = useModal('addOrganization');

	const {
		value: customers,
		loading: customersLoading,
		execute: onRefreshCustomers,
	} = useAsync(getCustomers);

	// Refresh list when add organization modal closes
	const prevAddOrgModalOpen = usePrevious(isAddOrgModalOpen);
	useEffect(() => {
		if (!isAddOrgModalOpen && prevAddOrgModalOpen) {
			onRefreshCustomers();
		}
	}, [isAddOrgModalOpen]);

	const {
		value: assets,
		execute: onRefreshAssets,
		resetValue: resetAssets,
	} = useAsync(getAssetsAdmin, { immediate: false });

	const { execute: onCreateServiceReport, loading: createLoading } = useAsync(
		createBlankAdminServiceReport,
		{ immediate: false },
	);

	const prevModalOpen = usePrevious(isCreateModalOpen);
	useEffect(() => {
		if (!isCreateModalOpen && prevModalOpen) {
			setForm(DEFAULT_FORM);
			setSelectedOrganization(null);
			resetAssets();
		}
	}, [isCreateModalOpen]);

	useEffect(() => {
		if (selectedOrganization?.id) {
			onRefreshAssets(selectedOrganization.id);
		}
	}, [selectedOrganization?.id]);

	const ASSET_OPTIONS = assets?.map((asset) => ({
		label: `${asset?.manufacturer?.name || ''} ${asset?.model?.name || ''} (S/N: ${asset?.serialNumber || '-'})`,
		value: asset?.id,
		asset,
	}));

	// Group organizations: parent orgs first, followed by their children
	const groupedOrganizations = useMemo(() => {
		if (!customers || !Array.isArray(customers)) return [];

		const parentOrgs = customers.filter((org) => !org.parentOrganizationId);
		const childOrgs = customers.filter((org) => org.parentOrganizationId);

		const result = [];
		parentOrgs.forEach((parent) => {
			result.push({ ...parent, isParent: true });
			const children = childOrgs.filter(
				(child) => child.parentOrganizationId === parent.id,
			);
			children.forEach((child) => {
				result.push({ ...child, isChild: true });
			});
		});

		// Add any orphaned children (parent not in list)
		const addedChildIds = new Set(result.filter((o) => o.isChild).map((o) => o.id));
		childOrgs.forEach((child) => {
			if (!addedChildIds.has(child.id)) {
				result.push({ ...child, isChild: true });
			}
		});

		return result;
	}, [customers]);

	const selectedAsset =
		ASSET_OPTIONS?.find((o) => o.value === form?.asset) || null;

	const handleOpenCreateModal = (organization, e) => {
		e?.stopPropagation();
		setSelectedOrganization(organization);
		setForm({
			...DEFAULT_FORM,
			customer: organization.id,
			invoiceNumber: `${organization.name?.substring(0, 3).toUpperCase()}-${Math.floor(100000 + Math.random() * 900000)}`,
		});
		setCreateModalOpen(true);
	};

	const handleCreateServiceReport = () => {
		onCreateServiceReport(
			{
				name: form?.name,
				asset: form?.asset,
				organizationId: form?.customer,
				fileName: form?.fileName,
				completedOn: form?.completedOn,
				type: form?.type,
				invoiceNumber: form?.invoiceNumber,
			},
			(payload) => {
				setCreateModalOpen(false);
				navigate(`/admin/service-report/${payload?.id}`);
			},
		);
	};

	return (
		<MainContent>
			<div className="flex items-center justify-between mb-4">
				<PageHeading divider={false}>Super Admin - Organizations</PageHeading>
				<Button onClick={() => onOpenAddOrgModal(true)}>
					<PlusIcon className="h-4 w-4 mr-2" />
					Add Organization
				</Button>
			</div>

			<DataTable
				columns={[
					{
						header: 'Organization Name',
						accessorKey: 'name',
						cell: (info) => {
							const row = info?.row?.original;
							return (
								<div className={`flex items-center ${row?.isChild ? 'pl-6' : ''}`}>
									{row?.isChild && (
										<span className="text-gray-400 mr-2">└</span>
									)}
									<span className={`font-medium ${row?.isChild ? 'text-gray-600' : ''}`}>
										{info?.getValue() || '-'}
									</span>
								</div>
							);
						},
					},
					{
						header: 'Type',
						accessorKey: 'type',
						cell: (info) => info?.getValue() || '-',
					},
					{
						header: 'Status',
						accessorKey: 'active',
						cell: (info) => (
							<span
								className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
									info?.getValue()
										? 'bg-green-100 text-green-800'
										: 'bg-gray-100 text-gray-800'
								}`}
							>
								{info?.getValue() ? 'Active' : 'Inactive'}
							</span>
						),
					},
					{
						header: 'Contract',
						accessorKey: 'contract',
						cell: (info) => info?.getValue() || '-',
					},
					{
						header: 'Actions',
						accessorKey: 'id',
						cell: (info) => {
							const rowOrgId = info?.row?.original?.id;
							const isCurrentOrg = currentOrganization?.id === rowOrgId;

							return (
								<div className="flex items-center gap-2">
									<button
										onClick={(e) => {
											e.stopPropagation();
											if (!isCurrentOrg) {
												switchOrganization(rowOrgId);
											}
										}}
										disabled={isCurrentOrg}
										className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md ${
											isCurrentOrg
												? 'bg-gray-100 text-gray-400 cursor-not-allowed'
												: 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
										}`}
									>
										{isCurrentOrg ? 'Current' : 'Enter Organization'}
									</button>
								</div>
							);
						},
					},
				]}
				data={{ rows: groupedOrganizations }}
				onRow={(row) => navigate(`/superadmin/organization/${row?.original?.id}`)}
				usePagination={false}
				useExport={false}
				hasCheckboxes={false}
				manualSorting={false}
				loading={customersLoading === 'pending'}
			/>

			<Modal
				title={`Create Service Report - ${selectedOrganization?.name || ''}`}
				open={isCreateModalOpen}
				onClose={() => setCreateModalOpen(false)}
			>
				<div className="grid grid-cols-2 gap-4">
					<div className="col-span-2">
						<TemplateReactSelect
							name="Asset"
							options={ASSET_OPTIONS}
							value={selectedAsset}
							onChange={(index, asset) => {
								const reportName = `${selectedOrganization?.name} ${asset?.asset?.manufacturer?.name || ''} ${asset?.asset?.model?.name || ''}`;
								setForm({
									...form,
									asset: asset?.value,
									name: reportName,
									fileName: `Scipods - ${reportName} - ${moment().format('MMDDYYYY')}.pdf`,
								});
							}}
						/>
					</div>

					<TemplateField
						name="Report Name"
						value={form?.name}
						onChange={(index, name) => setForm({ ...form, name })}
					/>

					<TemplateField
						name="File Name"
						value={form?.fileName}
						onChange={(index, fileName) => setForm({ ...form, fileName })}
					/>

					<TemplateDate
						name="Completed On"
						value={form?.completedOn}
						onChange={(index, completedOn) =>
							setForm({ ...form, completedOn })
						}
					/>

					<TemplateReactSelect
						name="Type"
						options={TYPE_OPTIONS}
						value={TYPE_OPTIONS?.find((o) => o.value === form?.type) || null}
						onChange={(index, type) =>
							setForm({ ...form, type: type?.value })
						}
					/>

					<TemplateField
						name="Invoice Number"
						value={form?.invoiceNumber}
						onChange={(index, invoiceNumber) =>
							setForm({ ...form, invoiceNumber })
						}
					/>

					<div className="col-span-2 flex justify-end mt-4">
						<Button
							onClick={handleCreateServiceReport}
							disabled={
								!form?.asset || !form?.name || createLoading === 'pending'
							}
						>
							{createLoading === 'pending'
								? 'Creating...'
								: 'Create Service Report'}
						</Button>
					</div>
				</div>
			</Modal>

			<AddOrganizationModal />
		</MainContent>
	);
};

export default OrganizationsList;
